import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { storageService } from './storageService';
import { dogService } from './dogService';
import { DogPhoto } from '@/types/dog';

/**
 * 犬の写真管理サービスクラス
 */
export class PhotoService {
  /**
   * 写真メタデータの作成
   */
  async createPhotoMetadata(
    dogId: string,
    fileName: string,
    fileUrl: string,
    userId: string,
    isMain = false
  ): Promise<DogPhoto> {
    try {
      // ドキュメントIDを生成
      const photoRef = doc(collection(db, 'dogs', dogId, 'photos'));
      const photoId = photoRef.id;

      const photo: DogPhoto = {
        id: photoId,
        dogId,
        fileName,
        fileUrl,
        isMain,
        uploadedAt: Timestamp.now(),
      };

      await setDoc(photoRef, photo);

      // メイン写真の場合、Dogのメタデータも更新
      if (isMain) {
        await this.updateDogMainPhoto(dogId, fileUrl, userId);
      }

      return photo;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`写真メタデータの作成に失敗しました: ${error.message}`);
      }
      throw new Error('写真メタデータの作成に失敗しました');
    }
  }

  /**
   * 個体の写真一覧取得
   */
  async getPhotos(dogId: string, userId: string): Promise<DogPhoto[]> {
    try {
      // 権限チェック: 個体の所有者確認
      const dog = await dogService.getDog(dogId, userId);
      if (!dog) {
        throw new Error('個体が見つかりません');
      }

      const q = query(
        collection(db, 'dogs', dogId, 'photos'),
        orderBy('uploadedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const photos: DogPhoto[] = [];

      querySnapshot.forEach((doc) => {
        photos.push(doc.data() as DogPhoto);
      });

      return photos;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`写真一覧の取得に失敗しました: ${error.message}`);
      }
      throw new Error('写真一覧の取得に失敗しました');
    }
  }

  /**
   * 写真削除（FirestoreとStorage両方）
   */
  async deletePhoto(photoId: string, dogId: string, userId: string): Promise<void> {
    try {
      // 権限チェック: 個体の所有者確認
      const dog = await dogService.getDog(dogId, userId);
      if (!dog) {
        throw new Error('個体が見つかりません');
      }

      // 写真メタデータ取得
      const photoRef = doc(db, 'dogs', dogId, 'photos', photoId);
      const photoSnap = await getDoc(photoRef);

      if (!photoSnap.exists()) {
        throw new Error('写真が見つかりません');
      }

      const photo = photoSnap.data() as DogPhoto;

      // Storageから削除
      await storageService.deleteFile(photo.fileUrl);

      // Firestoreから削除
      await deleteDoc(photoRef);

      // メイン写真だった場合、Dogのメタデータを更新
      if (photo.isMain) {
        // 残りの写真を取得
        const remainingPhotos = await this.getPhotos(dogId, userId);

        if (remainingPhotos.length > 0) {
          // 最新の写真をメイン写真に設定
          await this.setMainPhoto(remainingPhotos[0].id, dogId, userId);
        } else {
          // 写真が全て削除された場合、mainPhotoUrlをクリア
          await this.updateDogMainPhoto(dogId, undefined, userId);
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`写真の削除に失敗しました: ${error.message}`);
      }
      throw new Error('写真の削除に失敗しました');
    }
  }

  /**
   * メイン写真の設定
   */
  async setMainPhoto(photoId: string, dogId: string, userId: string): Promise<void> {
    try {
      // 権限チェック: 個体の所有者確認
      const dog = await dogService.getDog(dogId, userId);
      if (!dog) {
        throw new Error('個体が見つかりません');
      }

      // 写真メタデータ取得
      const photoRef = doc(db, 'dogs', dogId, 'photos', photoId);
      const photoSnap = await getDoc(photoRef);

      if (!photoSnap.exists()) {
        throw new Error('写真が見つかりません');
      }

      const photo = photoSnap.data() as DogPhoto;

      // 既存のメイン写真を解除
      const photos = await this.getPhotos(dogId, userId);
      for (const p of photos) {
        if (p.isMain && p.id !== photoId) {
          const oldMainPhotoRef = doc(db, 'dogs', dogId, 'photos', p.id);
          await updateDoc(oldMainPhotoRef, { isMain: false });
        }
      }

      // 新しいメイン写真を設定
      await updateDoc(photoRef, { isMain: true });

      // DogのmainPhotoUrlを更新
      await this.updateDogMainPhoto(dogId, photo.fileUrl, userId);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`メイン写真の設定に失敗しました: ${error.message}`);
      }
      throw new Error('メイン写真の設定に失敗しました');
    }
  }

  /**
   * 写真アップロードとメタデータ保存を一度に実行
   */
  async uploadPhotoWithMetadata(
    file: File,
    dogId: string,
    userId: string
  ): Promise<DogPhoto> {
    try {
      // 個体の所有者確認
      const dog = await dogService.getDog(dogId, userId);
      if (!dog) {
        throw new Error('個体が見つかりません');
      }

      // Storageにアップロード
      const fileUrl = await storageService.uploadPhoto(file, dogId, userId);

      // 既存の写真を確認（最初の写真はメイン写真に設定）
      const existingPhotos = await this.getPhotos(dogId, userId);
      const isMain = existingPhotos.length === 0;

      // メタデータを保存
      const photo = await this.createPhotoMetadata(
        dogId,
        file.name,
        fileUrl,
        userId,
        isMain
      );

      return photo;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`写真アップロードに失敗しました: ${error.message}`);
      }
      throw new Error('写真アップロードに失敗しました');
    }
  }

  /**
   * 複数写真の一括アップロード
   */
  async uploadMultiplePhotos(
    files: File[],
    dogId: string,
    userId: string
  ): Promise<DogPhoto[]> {
    const uploadedPhotos: DogPhoto[] = [];
    const uploadedUrls: string[] = [];

    try {
      // 個体の所有者確認
      const dog = await dogService.getDog(dogId, userId);
      if (!dog) {
        throw new Error('個体が見つかりません');
      }

      // 既存の写真を確認
      const existingPhotos = await this.getPhotos(dogId, userId);
      let isFirstPhoto = existingPhotos.length === 0;

      // 各ファイルをアップロード
      for (const file of files) {
        // Storageにアップロード
        const fileUrl = await storageService.uploadPhoto(file, dogId, userId);
        uploadedUrls.push(fileUrl);

        // メタデータを保存（最初の写真のみメイン写真に設定）
        const photo = await this.createPhotoMetadata(
          dogId,
          file.name,
          fileUrl,
          userId,
          isFirstPhoto
        );

        uploadedPhotos.push(photo);
        isFirstPhoto = false; // 2枚目以降はメイン写真にしない
      }

      return uploadedPhotos;
    } catch (error) {
      // エラー発生時はロールバック（アップロード済みの写真を削除）
      console.error('複数写真アップロード中にエラーが発生しました。ロールバックを実行します。');

      // アップロード済みの写真をStorageから削除
      for (const url of uploadedUrls) {
        try {
          await storageService.deleteFile(url);
        } catch (deleteError) {
          console.error('ロールバック中にエラーが発生しました:', deleteError);
        }
      }

      // アップロード済みのメタデータをFirestoreから削除
      for (const photo of uploadedPhotos) {
        try {
          const photoRef = doc(db, 'dogs', dogId, 'photos', photo.id);
          await deleteDoc(photoRef);
        } catch (deleteError) {
          console.error('ロールバック中にエラーが発生しました:', deleteError);
        }
      }

      if (error instanceof Error) {
        throw new Error(`複数写真のアップロードに失敗しました: ${error.message}`);
      }
      throw new Error('複数写真のアップロードに失敗しました');
    }
  }

  /**
   * DogのmainPhotoUrlを更新
   */
  private async updateDogMainPhoto(
    dogId: string,
    mainPhotoUrl: string | undefined,
    userId: string
  ): Promise<void> {
    await dogService.updateDog(dogId, { mainPhotoUrl } as any, userId);
  }
}

// シングルトンインスタンスをエクスポート
export const photoService = new PhotoService();
